import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ScanLine,
  Plus,
  Search,
  Check,
  AlertCircle,
} from 'lucide-react';
import { DataTable, type Column, type TableDensity } from '@/components/common/DataTable';
import { DensityToggle } from '@/components/common/DensityToggle';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { ErrorState } from '@/components/common/ErrorState';
import { SectionCard } from '@/components/common/SectionCard';
import { StatusTabs, type StatusTabOption } from '@/components/common/StatusTabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TooltipProvider } from '@/components/ui/tooltip';
import { barcodesService } from '@/services/barcodes';
import { inventoryService } from '@/services/inventory';
import type { BarcodeStatus, ScanContext, ValidateScanResult } from '@/types/barcodes';
import { fmt } from '@/lib/format';
import { useDebounced } from '@/hooks/useDebounced';
import { GenerateBarcodesSheet } from './GenerateBarcodesSheet';

type StatusFilter = 'all' | BarcodeStatus;

const STATUS_LOWER: Record<BarcodeStatus, string> = {
  ACTIVE: 'active',
  RETIRED: 'archived',
  REVOKED: 'revoked',
};

export function BarcodesPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [generateOpen, setGenerateOpen] = useState(false);
  const [density, setDensity] = useState<TableDensity>('compact');
  const debounced = useDebounced(search);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['barcodes', { code: debounced }],
    queryFn: () =>
      barcodesService.list({
        code: debounced || undefined,
        pageSize: 200,
      }),
  });

  // Items lookup so we can show the SKU when a code is pre-bound.
  const itemsQuery = useQuery({
    queryKey: ['inventory', 'items', { forBarcodeNames: true }],
    queryFn: () => inventoryService.list({ pageSize: 200 }),
    staleTime: 60_000,
  });
  const itemById = useMemo(() => {
    const m = new Map<string, NonNullable<typeof itemsQuery.data>['items'][number]>();
    for (const it of itemsQuery.data?.items ?? []) m.set(it.id, it);
    return m;
  }, [itemsQuery.data]);

  const allRows = useMemo(() => data?.rows ?? [], [data]);

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = {
      all: allRows.length,
      ACTIVE: 0,
      RETIRED: 0,
      REVOKED: 0,
    };
    for (const b of allRows) c[b.status] += 1;
    return c;
  }, [allRows]);

  const filtered = useMemo(() => {
    if (filter === 'all') return allRows;
    return allRows.filter((b) => b.status === filter);
  }, [allRows, filter]);

  const tabs: StatusTabOption[] = [
    { value: 'all',     label: 'All',      count: counts.all },
    { value: 'ACTIVE',  label: 'Active',   count: counts.ACTIVE },
    { value: 'RETIRED', label: 'Retired',  count: counts.RETIRED },
    { value: 'REVOKED', label: 'Revoked',  count: counts.REVOKED },
  ];

  const columns: Column<(typeof allRows)[number]>[] = [
    {
      key: 'code',
      header: 'Code',
      cell: (r) => <span className="font-mono text-[11.5px]">{r.code}</span>,
    },
    {
      key: 'item',
      header: 'Pre-bound SKU',
      cell: (r) => {
        if (!r.itemId) return <span className="text-[12px] text-muted-foreground">unbound</span>;
        const item = r.item ?? itemById.get(r.itemId);
        return (
          <div className="space-y-0.5">
            <div className="text-[12.5px]">{item?.name ?? '—'}</div>
            <div className="font-mono text-[10.5px] text-muted-foreground">{item?.sku ?? r.itemId.slice(0, 8)}</div>
          </div>
        );
      },
    },
    {
      key: 'consumed',
      header: 'Consumed',
      width: '110px',
      cell: (r) =>
        r.finishedGoodId ? (
          <Badge variant="success" className="text-[10px]">linked to FG</Badge>
        ) : (
          <span className="text-[12px] text-muted-foreground">unconsumed</span>
        ),
    },
    {
      key: 'scans',
      header: 'Scans',
      align: 'right',
      width: '80px',
      cell: (r) => <span className="font-mono text-[12.5px] tabular-nums">{fmt.number(r.scanCount ?? 0)}</span>,
    },
    {
      key: 'lastScan',
      header: 'Last scan at',
      width: '180px',
      cell: (r) =>
        r.lastScannedAt ? (
          <span className="font-mono text-[11.5px] tabular-nums text-muted-foreground">
            {fmt.dateTime(r.lastScannedAt)}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'generated',
      header: 'Generated at',
      width: '180px',
      cell: (r) => (
        <span className="font-mono text-[11.5px] tabular-nums text-muted-foreground">
          {fmt.dateTime(r.generatedAt)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '100px',
      cell: (r) => (
        <Badge variant={r.status === 'ACTIVE' ? 'success' : r.status === 'REVOKED' ? 'destructive' : 'muted'} className="text-[10px]">
          {STATUS_LOWER[r.status]}
        </Badge>
      ),
    },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">
        <ListPageHeader
          title="Barcodes & QR"
          count={counts.all}
          actions={
            <Button size="sm" onClick={() => setGenerateOpen(true)}>
              <Plus className="h-3.5 w-3.5" />
              Generate codes
            </Button>
          }
        />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <ScanCard />

          <div className="space-y-3 lg:col-span-2">
            <StatusTabs value={filter} onChange={(v) => setFilter(v as StatusFilter)} options={tabs} />

            <div className="flex items-center justify-between gap-3">
              <div className="relative w-full max-w-sm">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by code…"
                  className="h-8 pl-8 text-[13px] font-mono"
                />
              </div>
              <DensityToggle value={density} onChange={setDensity} />
            </div>

            {error ? (
              <ErrorState error={error} onRetry={() => refetch()} />
            ) : (
              <DataTable
                columns={columns}
                rows={filtered}
                rowKey={(r) => r.id}
                isLoading={isLoading}
                density={density}
                emptyTitle={filter === 'all' ? 'No barcodes generated yet' : `No ${STATUS_LOWER[filter]} codes`}
                emptyDescription="Generate a batch ahead of receiving stock or labelling units."
                emptyAction={
                  filter === 'all' ? (
                    <Button size="sm" onClick={() => setGenerateOpen(true)}>
                      <Plus className="h-3.5 w-3.5" />
                      Generate codes
                    </Button>
                  ) : null
                }
              />
            )}
          </div>
        </div>

        <GenerateBarcodesSheet open={generateOpen} onOpenChange={setGenerateOpen} />
      </div>
    </TooltipProvider>
  );
}

// ── Scan card ─────────────────────────────────────────────────────────

function ScanCard() {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [context, setContext] = useState<ScanContext>('INWARD');
  const [result, setResult] = useState<ValidateScanResult | null>(null);

  const mutation = useMutation({
    mutationFn: () => barcodesService.validateScan({ code: code.trim(), context }),
    onSuccess: (res) => {
      setResult(res);
      qc.invalidateQueries({ queryKey: ['barcodes'] });
      if (res.valid) {
        toast.success(`Scan OK · ${context.toLowerCase()}`);
      }
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Scan failed');
      setResult(null);
    },
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!code.trim()) return;
    mutation.mutate();
  };

  return (
    <SectionCard
      className="lg:col-span-1"
      title={
        <span className="flex items-center gap-2">
          <ScanLine className="h-4 w-4 text-muted-foreground" /> Scan a code
        </span>
      }
      description="Type or wedge-scan — validates against the chosen context."
    >
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="scan-context" className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
            Context
          </Label>
          <Select value={context} onValueChange={(v) => setContext(v as ScanContext)}>
            <SelectTrigger id="scan-context" className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="INWARD">Inward (receive)</SelectItem>
              <SelectItem value="DISPATCH">Dispatch (ship)</SelectItem>
              <SelectItem value="RETURN">Return (receive back)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="scan-input" className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
            Code
          </Label>
          <Input
            id="scan-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="zfb_live_a1b2c3d4e5f6"
            className="h-9 font-mono text-[12.5px]"
            autoFocus
          />
        </div>

        <div className="flex gap-2">
          <Button type="submit" className="flex-1" size="sm" disabled={!code.trim() || mutation.isPending}>
            {mutation.isPending ? 'Looking up…' : 'Validate'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setCode('');
              setResult(null);
            }}
          >
            Reset
          </Button>
        </div>
      </form>

      {result && (
        <div
          className={
            result.valid
              ? 'mt-4 rounded-md border border-success/30 bg-success/5 p-3'
              : 'mt-4 rounded-md border border-destructive/25 bg-destructive/5 p-3'
          }
        >
          <div className="flex items-start gap-2">
            {result.valid ? (
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
            ) : (
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            )}
            <div className="space-y-1 text-[12.5px]">
              <div className={result.valid ? 'font-medium text-success' : 'font-medium text-destructive'}>
                {result.valid ? `Valid for ${result.context.toLowerCase()}` : 'Scan rejected'}
              </div>
              <div className="font-mono text-[11px] text-foreground/80">{result.code}</div>
              {result.reason && (
                <div className="text-[11.5px] text-muted-foreground">{result.reason}</div>
              )}
              {result.finishedGood && (
                <div className="text-[11px] text-muted-foreground">
                  Linked to FG · status {result.finishedGood.status.toLowerCase()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}
